import React, { ReactNode } from 'react';
import { FieldErrors } from 'react-hook-form';
import styled from 'styled-components/macro';
import SelectInput, { Select, ISelectInputOption, LabelText } from 'shared/components/SelectInput';
import { getDaysInMonth } from 'date-fns';

const monthOptions: ISelectInputOption[] = [
  { label: 'Month', value: null },
  { label: 'January', value: '1' },
  { label: 'February', value: '2' },
  { label: 'March', value: '3' },
  { label: 'April', value: '4' },
  { label: 'May', value: '5' },
  { label: 'June', value: '6' },
  { label: 'July', value: '7' },
  { label: 'August', value: '8' },
  { label: 'September', value: '9' },
  { label: 'October', value: '10' },
  { label: 'November', value: '11' },
  { label: 'December', value: '12' },
];

const getDayOptions = (date: Date | undefined): ISelectInputOption[] => {
  const days = [{ label: 'Day', value: null }] as ISelectInputOption[];
  const daysInMonth = date ? getDaysInMonth(new Date(date)) : 31;
  for (let i = 1; i <= daysInMonth; i++) {
    days.push({ label: i.toString(), value: i });
  }
  return days;
};

const yearOptions = ((): ISelectInputOption[] => {
  const years = [];
  const currentYear = new Date().getFullYear();
  for (let i = currentYear; i >= 1970; i--) {
    years.push({
      label: `${i}`,
      value: i,
    });
  }
  return [{ label: 'Year', value: null }, ...years];
})();

interface IBirthDateSelectProps {
  name: string;
  label: string;
  errors?: FieldErrors<Record<string, unknown>>;
  value?: Date;
  onChange?: (date?: Date) => void;
}

const BirthDateSelect = ({ name, label, errors, value, onChange }: IBirthDateSelectProps): JSX.Element => {
  const renderOptions = (options: ISelectInputOption[]): ReactNode => {
    return options.map(({ label, value: optionValue }) => (
      <option key={label} value={optionValue} disabled={!optionValue && !!value}>
        {label}
      </option>
    ));
  };

  const handleClearDate = (): void => {
    if (onChange) onChange(undefined);
  };

  const handleMonthChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const date = value ? new Date(value) : new Date();
    date.setMonth(event.target.value === '0' ? 0 : Number(event.target.value) - 1);
    if (onChange) onChange(date);
  };
  const handleDayChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const date = value ? new Date(value) : new Date();
    date.setDate(Number(event.target.value || 1));

    if (onChange) onChange(date);
  };
  const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const date = value ? new Date(value) : new Date();
    if (!event.target.value) {
      if (onChange) onChange(undefined);
    } else {
      date.setFullYear(Number(event.target.value));

      if (onChange) onChange(date);
    }
  };

  const month = value ? new Date(value).getMonth() + 1 : '';
  const day = value ? new Date(value).getDate() : '';
  const year = value ? new Date(value).getFullYear() : '';

  return (
    <SelectInput
      name={name}
      label={(): ReactNode => (
        <StyledLabelText>
          {label}
          {!!value && <TextButton onClick={handleClearDate}>Clear</TextButton>}
        </StyledLabelText>
      )}
      errors={errors}
    >
      <BirthDateSelectContainer>
        <Select placeholder="Month" onChange={handleMonthChange} value={month}>
          {renderOptions(monthOptions)}
        </Select>
        <Select onChange={handleDayChange} value={day}>
          {renderOptions(getDayOptions(value))}
        </Select>
        <Select onChange={handleYearChange} value={year}>
          {renderOptions(yearOptions)}
        </Select>
      </BirthDateSelectContainer>
    </SelectInput>
  );
};

const BirthDateSelectContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  & select {
    flex: 1 1 auto;
    width: auto;

    &:nth-of-type(n + 2) {
      margin-left: 10px;
    }
  }
`;

const StyledLabelText = styled(LabelText)`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`;

const TextButton = styled.div`
  color: hsl(190, 55%, 49%);
  cursor: pointer;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
    color: hsl(190, 55%, 42%);
  }
`;

export default BirthDateSelect;
